import * as THREE from 'three'
import { createRoot, extend } from '@react-three/fiber'
import React, { useState, useEffect, useRef } from 'react'
import { createRoot as createReactRoot } from 'react-dom/client'
import './styles.css'
import App from './App'
import Dashboard from './Dashboard'
import VoiceChat from './VoiceChat'

// This is essential - extends THREE objects to be used as JSX components
extend(THREE)

// Better SVG Settings Icon
const GearIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// Create a wrapper component for the entire app
const AppWrapper = () => {
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [conversationState, setConversationState] = useState('idle');
  const [audioData, setAudioData] = useState(null);
  
  const canvasRef = useRef(null);
  const r3fRootRef = useRef(null);
  const lastDebugLog = useRef(0);

  // Visibility flags for UI elements
  const SHOW_SETTINGS_BUTTON = false; // Set to true to show settings button
  const SHOW_VOICE_CHAT_BUTTON = true; // Set to true to show play/stop button

  useEffect(() => {
    // Initialize R3F after the canvas is mounted
    if (canvasRef.current && !r3fRootRef.current) {
      const r3fRoot = createRoot(canvasRef.current);
      
      r3fRoot.configure({
        linear: true,
        flat: true,
        dpr: [1, 2],
        camera: { fov: 30, position: [0, 0, 8] }
      });

      r3fRootRef.current = r3fRoot;
    }

    // Update R3F scene when conversation state or audio data changes
    if (r3fRootRef.current) {
      r3fRootRef.current.render(<App conversationState={conversationState} audioData={audioData} />);
      
      // Debug logging (throttled)
      const now = Date.now();
      if (now - lastDebugLog.current > 3000) {
        console.log('🔄 AppWrapper updating R3F with:', {
          conversationState,
          audioData: audioData ? {
            volume: audioData.volume?.toFixed(2),
            intensity: audioData.intensity?.toFixed(2),
            frequency: audioData.frequency?.toFixed(2),
            speechRate: audioData.speechRate?.toFixed(2)
          } : null
        });
        lastDebugLog.current = now;
      }
    }
  }, [conversationState, audioData]);

  const handleConversationStateChange = (newState) => {
    setConversationState(newState);
    console.log('📞 Conversation state changed to:', newState);
  };

  const handleAudioAnalysis = (newAudioData) => {
    setAudioData(newAudioData);
    // Detailed audio data logging (throttled)
    const now = Date.now();
    if (now - lastDebugLog.current > 2500) {
      console.log('🎧 AppWrapper received audio data:', newAudioData);
    }
  };

  return (
    <>
      {/* 3D Canvas */}
      <canvas ref={canvasRef} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} />
      
      {/* Floating Dashboard Button */}
      {SHOW_SETTINGS_BUTTON && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 999
        }}>
          <button
            onClick={() => setIsDashboardOpen(true)}
            style={{
              background: '#1a1a1a',
              border: '1.5px solid white',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.borderColor = 'white';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#1a1a1a';
              e.target.style.borderColor = 'white';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <GearIcon />
          </button>
        </div>
      )}
      
      {/* Voice Chat Component - Outside R3F Canvas */}
      <VoiceChat 
        onConversationStateChange={handleConversationStateChange} 
        onAudioAnalysis={handleAudioAnalysis}
        showButton={SHOW_VOICE_CHAT_BUTTON}
      />
      
      {/* Dashboard Overlay */}
      <Dashboard 
        isOpen={isDashboardOpen} 
        onClose={() => setIsDashboardOpen(false)}
      />
    </>
  );
};

// Create React root for the wrapper
const reactRoot = createReactRoot(document.getElementById('root'));
reactRoot.render(<AppWrapper />);
